<template>
  <div class="container mx-auto px-4 py-8">
    <h1 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">Security Update Subscribers</h1>
    <p class="text-sm text-gray-500 dark:text-gray-400 mb-6">Verified email subscribers who receive notifications about security updates.</p>

    <DataTable
      :columns="columns"
      :data="subscribers"
      :loading="loading"
      :error="error"
      :pagination="pagination"
      @page-change="handlePageChange"
    />
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import axios from 'axios'
import DataTable from '@/components/DataTable.vue'

const getAPIURL = () => {
  const envUrl = process.env.VUE_APP_API_URL || 'http://127.0.0.1:10000'
  const cleanUrl = envUrl.replace(/\/$/, '')
  return cleanUrl.endsWith('/api/v1') ? cleanUrl : `${cleanUrl}/api/v1`
}

export default {
  name: 'SubscribersView',
  components: { DataTable },
  setup() {
    const store = useStore()
    const route = useRoute()
    const tenantId = () => route.params.tenantId || store.getters.currentTenantId
    const headers = () => ({ Authorization: `Bearer ${store.state.token}` })

    const subscribers = ref([])
    const loading = ref(false)
    const error = ref(null)
    const pagination = ref({
      currentPage: 1,
      totalPages: 1,
      entriesPerPage: 25,
      totalItems: 0,
      orderBy: 'created_at',
      sortDirection: 'desc',
      search: '',
    })

    const columns = [
      { key: 'email', label: 'Email', sortable: true },
      { key: 'name', label: 'Name', sortable: true, formatter: v => v || '—' },
      { key: 'CreatedAt', label: 'Subscribed', sortable: true, formatter: v => v ? new Date(v).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '' },
    ]

    const fetchSubscribers = async (pag = null) => {
      const tid = tenantId()
      if (!tid) return
      loading.value = true
      error.value = null
      try {
        const p = pag || pagination.value
        const res = await axios.get(`${getAPIURL()}/admin/tenants/${tid}/subscribers`, {
          headers: headers(),
          params: {
            page: p.currentPage,
            per_page: p.entriesPerPage,
            order_by: p.orderBy,
            sort_direction: p.sortDirection,
            search: p.search || '',
          }
        })
        subscribers.value = res.data?.data || []
        pagination.value = {
          currentPage: res.data?.currentPage || 1,
          totalPages: res.data?.totalPages || 1,
          entriesPerPage: res.data?.entriesPerPage || p.entriesPerPage,
          totalItems: res.data?.totalEntries || 0,
          orderBy: res.data?.orderBy || p.orderBy,
          sortDirection: res.data?.sortDirection || p.sortDirection,
          search: res.data?.search || '',
        }
      } catch (e) {
        console.error('Failed to fetch subscribers', e)
        error.value = 'Failed to load subscribers'
      } finally {
        loading.value = false
      }
    }

    const handlePageChange = (params) => {
      const newPag = {
        currentPage: params.page || 1,
        entriesPerPage: params.entriesPerPage || pagination.value.entriesPerPage,
        orderBy: params.orderBy || pagination.value.orderBy,
        sortDirection: params.sortDirection || pagination.value.sortDirection,
        search: params.search ?? pagination.value.search,
      }
      fetchSubscribers(newPag)
    }

    onMounted(() => fetchSubscribers())

    return { subscribers, loading, error, pagination, columns, handlePageChange }
  }
}
</script>
